import { Injectable, NgZone, OnDestroy } from '@angular/core';
import {
  BehaviorSubject,
  Observable,
  Subject,
  distinctUntilChanged,
  interval,
  map,
  shareReplay,
  takeUntil,
} from 'rxjs';
import {
  BillingPhase,
  NETWORK_THRESHOLDS,
} from '../models/network-health.model';

/**
 * BillingGuardService
 *
 * Owns the Process Order execution lock.
 *
 * Responsibilities:
 * - Prevents double-submission of the billing request
 * - Drives a billing-elapsed timer while a request is in flight
 * - Derives BillingPhase (IDLE / PROCESSING / SLOW_WARN / CRITICAL)
 * - Cleans up all timers when unlockOrder() is called
 *
 * IMPORTANT:
 * - This service does NOT make any HTTP calls.
 * - It does NOT modify any business logic in DashboardComponent.
 * - It only tracks WHETHER a billing operation is currently executing.
 *
 * Usage:
 *   // Call at the start of processPayment()
 *   if (!billingGuardService.lockOrder()) return;
 *
 *   // Call in both next: and error: handlers of placeOrder()
 *   billingGuardService.unlockOrder();
 */
@Injectable({ providedIn: 'root' })
export class BillingGuardService implements OnDestroy {
  private readonly destroy$ = new Subject<void>();
  private readonly unlocked$ = new Subject<void>();

  // ─── Internal state ───────────────────────────────────────────────────────

  private readonly isProcessingSubject$ = new BehaviorSubject<boolean>(false);
  private readonly elapsedMsSubject$ = new BehaviorSubject<number>(0);
  private lockStartTime = 0;

  // ─── Public Observables ───────────────────────────────────────────────────

  /** True while a billing request is in flight. Drives button [disabled]. */
  readonly isProcessingOrder$: Observable<boolean> =
    this.isProcessingSubject$.asObservable().pipe(
      distinctUntilChanged(),
      shareReplay(1)
    );

  /** Elapsed milliseconds since lockOrder() was called. */
  readonly billingElapsedMs$: Observable<number> =
    this.elapsedMsSubject$.asObservable();

  /**
   * Current billing phase derived from elapsed time.
   * Components subscribe to this to show phase-appropriate messages.
   */
  readonly billingPhase$: Observable<BillingPhase> =
    this.elapsedMsSubject$.pipe(
      map((elapsedMs) => {
        if (!this.isProcessingSubject$.value) return BillingPhase.IDLE;
        if (elapsedMs >= NETWORK_THRESHOLDS.BILLING_CRITICAL_MS)
          return BillingPhase.CRITICAL;
        if (elapsedMs >= NETWORK_THRESHOLDS.BILLING_WARN_MS)
          return BillingPhase.SLOW_WARN;
        return BillingPhase.PROCESSING;
      }),
      distinctUntilChanged(),
      shareReplay(1)
    );

  constructor(private readonly ngZone: NgZone) {}

  // ─── Public API ───────────────────────────────────────────────────────────

  /**
   * Acquires the billing lock.
   *
   * @returns true if the lock was acquired (caller should proceed).
   *          false if a billing request is already in flight (caller must return immediately).
   */
  lockOrder(): boolean {
    if (this.isProcessingSubject$.value) {
      // Already locked — this is the double-click guard
      return false;
    }

    this.lockStartTime = Date.now();
    this.isProcessingSubject$.next(true);
    this.elapsedMsSubject$.next(0);
    this.startElapsedTimer();
    return true;
  }

  /**
   * Releases the billing lock.
   * Must be called in BOTH success and error handlers of the API call.
   */
  unlockOrder(): void {
    this.unlocked$.next(); // stops the elapsed timer
    this.isProcessingSubject$.next(false);
    this.elapsedMsSubject$.next(0);
    this.lockStartTime = 0;
  }

  ngOnDestroy(): void {
    this.unlockOrder();
    this.destroy$.next();
    this.destroy$.complete();
    this.isProcessingSubject$.complete();
    this.elapsedMsSubject$.complete();
  }

  // ─── Internal ─────────────────────────────────────────────────────────────

  /**
   * Starts a 500ms polling interval to update elapsedMs.
   * The interval is automatically destroyed when unlockOrder() is called
   * (via unlocked$ Subject) or when the service is destroyed.
   */
  private startElapsedTimer(): void {
    // Reset unlocked$ so the new takeUntil fires cleanly
    interval(500)
      .pipe(
        takeUntil(this.unlocked$),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        const elapsedMs = Date.now() - this.lockStartTime;
        this.ngZone.run(() => this.elapsedMsSubject$.next(elapsedMs));
      });
  }
}
